import { findOptionsForSelectFilter } from '../../util/search';
import { createResourceLocatorString } from '../../util/routes';
import { getUserSubscriptionLevel } from '../../util/types';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import config from '../../config';
import { post } from '../../util/api';
import { POPUP_TIMEOUT } from '../EditListingPage/EditListingPage.duck';
import { mailingService } from '../../services/services';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';

const sharetribeSdk = require('sharetribe-flex-sdk');
const sdk = sharetribeSdk.createInstance({
  clientId: process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID,
});

const isDraftTextEmpty = (fieldValue) => {
  const fieldValueOldFormat = !!fieldValue && fieldValue.indexOf("blocks") === -1;
  const fieldValueToObject = !!fieldValue && !fieldValueOldFormat && JSON.parse(fieldValue)
  return !!fieldValueToObject && fieldValue.indexOf("blocks") !== -1 && fieldValueToObject?.blocks?.every(b => b.text.trim() === '');
}

export const turnCustomerIntoProviderMaybe = hasProviderMarker => {
  // only turn into service provider if "Become a service provider was selected"
  if (hasProviderMarker) {

    return sdk.currentUser.show()
      .then(res => {

        const subscriptionLevel = res.data.data?.attributes?.profile?.privateData?.subscriptionProduct?.metadata?.level || null

        return sdk.currentUser
          .updateProfile({
            publicData: {
              userType: 'service-provider',
              subscriptionLevel,
            },
            privateData: {
              subscriptionPlan: null,
              subscriptionProduct: null,
              subscription_plan_name: null,
              subscriptionId: null,
            },
          })
      })
      .catch(e => {
        console.log(e);
        return Promise.resolve('error');
      });
  } else {
    return Promise.resolve('success');
  }
};


export const getListingValuesBeforePublish = (listing, currentUser) => {

  const { title = null, description = null, publicData = {}, images = [] } = listing?.attributes || {};

  const providerFields = [
    // Profile tab
    { value: currentUser?.profileImage?.id?.uuid || images?.length, key: "logo" },
    { value: title, key: "EditListingDescriptionForm.companyName" },
    { value: !!publicData?.useDisplayName?.[0] ? publicData?.displayName : true, key: "name" },
    { value: !!publicData?.useDisplayName?.[0] ? publicData?.displaySurname : true, key: "surname" },
    { value: publicData?.shortDescription, key: "businessTagLine" },
    { value: description, key: "businessDescription" },
    { value: publicData?.website, key: "website" },
    { value: publicData?.address, key: "location" },
    { value: publicData?.phoneNumber, key: "phoneNumber" },
    { value: publicData?.productType?.[0], key: "whichSectorsDoYouServe" },
    { value: publicData?.providerType, key: "providerType" },
    { value: publicData?.startupAsk, key: "startupAsk" },
    { value: publicData?.fundingStatus, key: "fundingStatus" },
    { value: publicData?.category?.[0], key: "category" },
    { value: publicData?.subcategory?.[0], key: "subcategory" },
    { value: publicData?.geographicalFocus?.[0], key: "geographicalFocus" },
    { value: publicData?.companySize, key: "companySize" },
    { value: publicData?.operatingSince, key: "numberOfYears" },
    { value: publicData?.questions?.[0]?.question, key: "questions" },
    { value: publicData?.paymentMethod?.[0], key: "getPaid" },
    { value: publicData?.referralLink, key: "referralLink" },
    // Extra details tab
    { value: images?.[0], key: "images" },
    { value: publicData?.differentiationFromCompetitors?.[0], key: "differentiationFromCompetitors" },
    { value: publicData?.associationTitle, key: "associationTitle" },
    { value: publicData?.associations?.[0], key: "associations" },
    { value: publicData?.qualificationTitle, key: "qualificationTitle" },
    { value: publicData?.qualifications?.[0], key: "qualifications" },
    { value: publicData?.files?.[0], key: "files" },
    { value: publicData?.videos?.[0], key: "videos" },
    // Posts tab
    { value: publicData?.news?.[0], key: "news" },
    { value: publicData?.content?.[0], key: "content" },
    { value: publicData?.events?.[0], key: "events" },
  ]

  const clientFields = [
    // Profile tab
    { value: currentUser?.profileImage?.id?.uuid || images?.length, key: "logo" },
    { value: title, key: "title" },
    { value: description, key: "description" },
    { value: publicData?.website, key: "website" },
    { value: publicData?.address, key: "address" },
    { value: publicData?.phoneNumber, key: "phoneNumber" },
    { value: publicData?.whatAreYouLookingFor, key: "whatAreYouLookingFor" },
    { value: publicData?.companySize, key: "companySize" },
    { value: publicData?.operatingSince, key: "operatingSince" },
    { value: publicData?.questions?.[0]?.question, key: "questions" },
    { value: publicData?.paymentMethod?.[0], key: "paymentMethod" },
    { value: publicData?.referralLink, key: "referralLink" },
    // Extra details tab
    { value: images?.[0], key: "images" },
    { value: publicData?.files?.[0], key: "files" },
    { value: publicData?.videos?.[0], key: "videos" },
  ]

  const isClient = currentUser?.attributes?.profile?.publicData?.userType === 'client';
  const emptyFields = isClient ? clientFields.filter(f => !f.value) : providerFields.filter(f => !f.value);

  return emptyFields
}

export const getListingValuesBeforeUpdate = (currentUser, images, values, isClient, isOffer, isStartupOffer= false) => {

  const descriptionKey = !isDraftTextEmpty(values?.description) && !!values?.description ? "description" : null;
  const offerDescriptionKey = !isDraftTextEmpty(values?.offerDescription) && !!values?.offerDescription ? "offerDescription" : null;
  const startupOfferDescriptionKey = !isDraftTextEmpty(values?.startupOfferDescription) && !!values?.startupOfferDescription ? "startupOfferDescription" : null;

  let providerValues = [
    { key: currentUser?.profileImage?.id?.uuid || images?.filter(img => typeof img.id === 'string' && img.id?.includes('logoPhoto-'))?.[0], label: "EditListingDescriptionForm.logo" },
    { key: values?.title, label: "EditListingDescriptionForm.companyName" },
    { key: !!values?.useDisplayName?.[0] ? values?.displayName : true, label: "EditListingDescriptionForm.name" },
    { key: !!values?.useDisplayName?.[0] ? values?.displaySurname : true, label: "EditListingDescriptionForm.surname" },
    { key: values?.shortDescription, label: "EditListingDescriptionForm.businessTagLine" },
    { key: descriptionKey, label: "EditListingDescriptionForm.businessDescription" },
    { key: values?.website, label: "EditListingDescriptionForm.website" },
    { key: values?.address, label: "EditListingDescriptionForm.location" },
    { key: values?.phoneNumber, label: "EditListingDescriptionForm.phoneNumber" },
    { key: values?.productType?.[0], label: "EditListingDescriptionForm.whichSectorsDoYouServe" },
    { key: values?.providerType?.[0], label: "EditListingDescriptionForm.providerTypeRequired" },
    { key: values?.category?.[0], label: "EditListingDescriptionForm.category" },
    { key: values?.subcategory?.[0], label: "EditListingDescriptionForm.subcategory" },
    { key: values?.geographicalFocus?.[0], label: "EditListingDescriptionForm.geographicalFocus" },
    { key: values?.companySize, label: "EditListingDescriptionForm.companySize" },
    { key: values?.operatingSince, label: "EditListingDescriptionForm.numberOfYears" },
    { key: values?.questions?.[0]?.question, label: "EditListingDescriptionForm.questions" },
    { key: values?.paymentMethod?.[0], label: "EditListingDescriptionForm.getPaid" },
  ];

  const startupOffer = [
    { key: values?.startupOfferTitle, label: "EditListingStartupOfferDescriptionForm.startupOfferTitle" },
    { key: values?.startupReward, label: "EditListingStartupOfferDescriptionForm.startupReward" },
    { key: startupOfferDescriptionKey, label: "EditListingStartupOfferDescriptionForm.startupOfferDescription" },
  ];

  if (values?.category?.length === 1 && values?.category?.[0] === 'investigation') {
    providerValues = providerValues?.filter(values => values.label !== 'EditListingDescriptionForm.subcategory')
  }

  const clientValues = [
    // { key: currentUser?.profileImage?.id?.uuid || images?.filter(img => typeof img.id === 'string' && img.id?.includes('logoPhoto-'))?.[0], label: "EditListingDescriptionForm.logo" },
    { key: values?.title, label: "EditListingDescriptionForm.companyName" },
    { key: values?.description, label: "EditListingDescriptionForm.businessDescription" },
    // { key: values?.website, label: "EditListingDescriptionForm.website" },
    // { key: values?.address, label: "EditListingDescriptionForm.location" },
    // { key: values?.phoneNumber, label: "EditListingDescriptionForm.phoneNumber" },
    // { key: values?.whatAreYouLookingFor, label: "EditListingDescriptionForm.whatServiceLookingFor" },
    // { key: values?.companySize, label: "EditListingDescriptionForm.companySize" },
    // { key: values?.operatingSince, label: "EditListingDescriptionForm.numberOfYears" },
    // { key: values?.questions?.[0]?.question, label: "EditListingDescriptionForm.questions" },
    // { key: values?.paymentMethod?.[0], label: "EditListingDescriptionForm.getPaid" },
  ];

  const offerValues = [
    { key: values?.title, label: "EditListingOfferDescriptionForm.name" },
    { key: values?.website, label: "EditListingDescriptionForm.website" },
    { key: values?.shortDescription, label: "EditListingDescriptionForm.businessTagLine" },
    { key: descriptionKey, label: "EditListingDescriptionForm.businessDescription" },
    { key: offerDescriptionKey, label: "EditListingOfferDescriptionForm.offerDescription" },
  ];

  if (isOffer) {
    return offerValues.filter(i => !i.key);
  } else if (isStartupOffer) {
    return startupOffer.filter(i => !i.key);
  } else if (isClient) {
    return clientValues.filter(i => !i.key)
  } else {
    return providerValues.filter(i => !i.key)
  }
}


export const createAndPublish = (
  values,
  imagesArray,
  listing,
  isDescriptionTab,
  currentUser,
  history,
  routes,
  setPublishSuccess,
  onUpdateProfile,
  isOffer,
  hasProviderMarker
) => {

  if (isOffer && !values?.files?.[0]) {
    values.files = null
  }

  const { publicData = {} } = listing?.attributes || {};

  const isProvider = hasProviderMarker || currentUser?.attributes?.profile?.publicData?.userType === 'service-provider';
  const isClient = currentUser?.attributes?.profile?.publicData?.userType === 'client';
  const title = values?.title || listing?.attributes?.title
  const description = values?.description || listing?.attributes?.description;
  let images = !!imagesArray?.length ? imagesArray : listing?.attributes?.images;

  const profileImage = images?.filter(img => img?.imageId?.uuid)?.[0] || [];

  const customDisplayName = !!values?.useDisplayName?.[0] && `${values?.displayName} ${values?.displaySurname} - ${title}` || publicData?.customDisplayName;

  const subcategoryPriceValuesArr = values?.subcategory?.map((item, i) => {
    const subcategoryOptions = findOptionsForSelectFilter('subcategory', config.custom.filters);
    const parentCategory = subcategoryOptions.filter(subCat => subCat.key === item)?.[0]?.category;

    return {
      key: values?.subcategory[i],
      parentCategory
    }
  })
  const subcategoryPriceValues = subcategoryPriceValuesArr || publicData?.subcategoryPriceValues;

  const firstImage = images?.filter(
    img => typeof img.id === 'string' && img.id?.includes('backgroundPhoto-')
  ) || [];
  const newImages = images?.filter(
    img => typeof img.id === 'string' && !img.id?.includes('backgroundPhoto-')
  ) || [];
  const savedImages = images?.filter(img => !!img?.id?.uuid) || [];
  images = [...firstImage, ...savedImages, ...newImages];

  const subscriptionLevel = getUserSubscriptionLevel(currentUser) || 1;

  const listingValues = {
    listingType: isOffer ? 'offer' : isProvider ? 'provider' : 'client',
    useDisplayName: values?.useDisplayName || publicData?.useDisplayName,
    displayName: values?.displayName || publicData?.displayName,
    displaySurname: values?.displaySurname || publicData?.displaySurname,
    customDisplayName,
    shortDescription: values?.shortDescription || publicData?.shortDescription,
    offerDescription: values?.offerDescription || publicData?.offerDescription,
    website: values?.website || publicData?.website,
    address: values?.address || publicData?.address,
    phoneNumber: values?.phoneNumber || publicData?.phoneNumber,
    productType: values?.productType || publicData?.productType,
    providerType: values?.providerType || publicData?.providerType,
    category: values?.category || publicData?.category,
    subcategory: values?.subcategory || publicData?.subcategory,
    subcategoryPriceValues,
    geographicalFocus: values?.geographicalFocus || publicData?.geographicalFocus,
    companySize: values?.companySize ? [values.companySize] : publicData?.companySize,
    operatingSince: values?.operatingSince ? [values.operatingSince] : publicData?.operatingSince,
    questions: values?.questions || publicData?.questions,
    paymentMethod: values?.paymentMethod || publicData?.paymentMethod,
    whatAreYouLookingFor: values?.whatAreYouLookingFor || publicData?.whatAreYouLookingFor,
    differentiationFromCompetitors: values?.differentiationFromCompetitors || publicData?.differentiationFromCompetitors,
    associationTitle: values?.associationTitle || publicData?.associationTitle,
    associations: values?.associations || publicData?.associations || null,
    qualificationTitle: values?.qualificationTitle || publicData?.qualificationTitle,
    qualifications: values?.qualifications || publicData?.qualifications || null,
    files: values?.files?.filter(f => !!f) || publicData?.files?.filter(f => !!f) || null,
    videos: values?.videos || publicData?.videos,
    news: values?.news || publicData?.news,
    content: values?.content || publicData?.content,
    events: values?.events || publicData?.events,
    backgroundPhotoIds: !!firstImage?.length && (images?.[0]?.id?.uuid || images?.[0]?.imageId?.uuid) || publicData?.backgroundPhotoIds || null,
    isOffer: isOffer,
    subscriptionLevel: subscriptionLevel,
    providerVerified: isProvider && !isOffer && !isClient ? false : null
  };

  if (isOffer) {

    const imagesArray = images?.map(image =>
      typeof image.id === 'string' ? image.imageId : image.id
    );

    return sdk.ownListings.createDraft({
      title,
      description,
      publicData: listingValues,
      images: imagesArray
    })
      .then((response) => {
        return sdk.ownListings.publishDraft({
          id: response?.data?.data?.id
        }, {
          expand: true
        })
      })
      .then((response) => {
        setPublishSuccess(true);
        return response
      })
      .then((response) => {
        post('/api/approve-client-listing', { listingId: response?.data?.data?.id?.uuid });
        setTimeout(() => {
          setPublishSuccess(false);
          const id = response?.data?.data?.id?.uuid;
          const slug = createSlug(response?.data?.data?.attributes?.title);
          return history.push(createResourceLocatorString('ListingPageVariant', routes, { id, slug, variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT }, {}));
        }, POPUP_TIMEOUT)
      })
      .catch(e => console.log(e));
  }
  else if (isDescriptionTab) {
    return sdk.ownListings.createDraft({
      title,
      description,
      publicData: listingValues
    })
      .then((response) => {
        return sdk.ownListings.publishDraft({
          id: response?.data?.data?.id
        }, {
          expand: true
        })
      })
      .then((response) => {
        onUpdateProfile({
          publicData: {
            profileCompleted: true,
            companyName:
              !!listingValues?.useDisplayName?.[0] ?
                `${listingValues?.displayName} ${listingValues?.displaySurname} - ${title}`
                : title.trim(),
            website: listingValues?.website,
            address: listingValues?.address,
            listingId: response?.data?.data?.id?.uuid,
          },
        })
        onUpdateProfile({
          profileImageId: hasProviderMarker ? currentUser?.profileImage?.id : (!!profileImage && profileImage?.imageId),
        })
        return response;
      })
      .then((response) => {
        setPublishSuccess(true);
        return response
      })
      .then((response) => {
        const isClient = response?.data?.data?.attributes?.publicData?.listingType === "client"
        const isProvider = response?.data?.data?.attributes?.publicData?.listingType === "provider"

        if(isClient) {
          post('/api/approve-client-listing', { listingId: response?.data?.data?.id?.uuid });
        }

        if(isProvider) {
          mailingService.sendMailProfileComplete({email: currentUser?.attributes?.email})
        }

        setTimeout(() => {
          setPublishSuccess(false);
          const id = response?.data?.data?.id?.uuid;
          const slug = createSlug(response?.data?.data?.attributes?.title);
          return history.push(createResourceLocatorString('ListingPageVariant', routes, { id, slug, variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT }, {}));
        }, POPUP_TIMEOUT)
      })
      .catch(e => console.log(e));

  } else {
    const imagesArray = images?.map(image =>
      typeof image.id === 'string' ? image.imageId : image.id
    );

    return sdk.ownListings.update({
      id: listing?.id?.uuid,
      publicData: listingValues,
      images: imagesArray
    })
      .then((response) => {
        return sdk.ownListings.publishDraft({
          id: response?.data?.data?.id
        }, {
          expand: true
        })
      })
      .then((response) => {
        setPublishSuccess(true);
        return response
      })
      .then((response) => {
        const isClient = response?.data?.data?.attributes?.publicData?.listingType === "client"
        const isProvider = response?.data?.data?.attributes?.publicData?.listingType === "provider"

        if (isClient) {
          post('/api/approve-client-listing', { listingId: response?.data?.data?.id?.uuid });
        }

        if(isProvider) {
          mailingService.sendMailProfileComplete({email: currentUser?.attributes?.email})
        }

        setTimeout(() => {
          setPublishSuccess(false);
          const id = response?.data?.data?.id?.uuid;
          const slug = createSlug(response?.data?.data?.attributes?.title);

          return history.push(createResourceLocatorString('ListingPageVariant', routes, { id, slug, variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT }, {}));
        }, POPUP_TIMEOUT)
      })
      .catch(e => console.log(e));
  }
}
